import _ from 'lodash';
import React, {ComponentType, PropsWithChildren, ReactNode} from 'react';

import {Plural, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {ConversationBaseModel} from '../../../../src/models/api/conversationModel';
import {ConversationBasesSelector} from '../../../../src/store/data/conversations/conversationsSelectors';
import {getFindTeammate} from '../../../../src/store/data/teammates/teammatesSelectors';
import {CellsContextSelector} from '../../../../src/store/panels/viewer/conversations/viewerPanelConversationsSelectors';
import {createSelector} from '../../../helpers/reselectHelpers';
import {
  ConversationInboxesFormatter,
  ConversationInboxesFormatterProps,
} from './conversationInboxesFormatter';

/*
 * Constants.
 */

export enum TriageActionsEnum {
  ARCHIVE = 'ARCHIVE',
  TRASH = 'TRASH',
  SNOOZE = 'SNOOZE',
  SPAM = 'SPAM',
}

type TriageActionRenderers = {[key in TriageActionsEnum]: ReactNode};
type TriageActionTitleRenderers = {
  [key in TriageActionsEnum]: (inboxes: ReadonlyArray<InboxModel>) => ReactNode;
};

const inboxesPartProps: Omit<ConversationInboxesFormatterProps, 'conversationInboxes'> = {
  renderDefault: () => <Trans id="triageaction-teammateinbox">Teammate inbox</Trans>,
  renderSingleInbox: (inbox) => inbox.name,
  renderManyInboxes: (inboxes) => <Trans id="triageaction-manyinboxes">{inboxes.length} inboxes</Trans>,
};

/* eslint-disable react/jsx-props-no-spreading */
export const triageActionDialogTitleRenderers: TriageActionTitleRenderers = {
  [TriageActionsEnum.ARCHIVE]: (inboxes) => (
    <Trans id="triage-team-confirmation-title-archive">
      Archive in <ConversationInboxesFormatter {...inboxesPartProps} conversationInboxes={inboxes} />
    </Trans>
  ),
  [TriageActionsEnum.SNOOZE]: (inboxes) => (
    <Trans id="triage-team-confirmation-title-snooze">
      Snooze in <ConversationInboxesFormatter {...inboxesPartProps} conversationInboxes={inboxes} />
    </Trans>
  ),
  [TriageActionsEnum.TRASH]: (inboxes) => (
    <Trans id="triage-team-confirmation-title-trash">
      Trash in <ConversationInboxesFormatter {...inboxesPartProps} conversationInboxes={inboxes} />
    </Trans>
  ),
  [TriageActionsEnum.SPAM]: (inboxes) => (
    <Trans id="triage-team-confirmation-title-spam">
      Mark as spam in <ConversationInboxesFormatter {...inboxesPartProps} conversationInboxes={inboxes} />
    </Trans>
  ),
};

export const triageActionDialogConfirmRenderer: TriageActionRenderers = {
  [TriageActionsEnum.ARCHIVE]: <Trans id="triage-team-confirmation-ok-archive">Archive</Trans>,
  [TriageActionsEnum.SNOOZE]: <Trans id="triage-team-confirmation-ok-snooze">Snooze</Trans>,
  [TriageActionsEnum.TRASH]: <Trans id="triage-team-confirmation-ok-trash">Trash</Trans>,
  [TriageActionsEnum.SPAM]: <Trans id="triage-team-confirmation-ok-spam">Mark as spam</Trans>,
};

export const triageActionLearnMoreLink = `https://help.front.com/t/h7h5yhx`;

interface TriageBodyRendererOptions {
  props: TriageTeamConfirmationDialogInheritableProps;
  BoldWrapper: ComponentType<PropsWithChildren<unknown>>;
  trailingPart?: ReactNode;
}

export interface TriageTeamConfirmationDialogAssignProps {
  isUserAssignee: boolean;
  assignee: TeammateModel | undefined;
}

export interface TriageTeamConfirmationDialogInheritableProps {
  conversations: ReadonlyArray<ConversationBaseModel>;
  inboxes: ReadonlyArray<InboxModel>;
  assignProps: TriageTeamConfirmationDialogAssignProps;
  action: TriageActionsEnum;
}

function renderArchiveBody(options: TriageBodyRendererOptions) {
  const {props, BoldWrapper, trailingPart} = options;
  const {inboxes, conversations} = props;
  const {assignee, isUserAssignee} = props.assignProps;

  const conversationsCount = conversations.length;

  if (conversationsCount > 1) {
    return (
      <Plural
        id="inboxes-triage-archive-body-plural"
        value={inboxes.length}
        one={
          <>
            Your teammates will see {conversationsCount} conversations as archived in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not archive in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Your teammates will see {conversationsCount} conversations as archived in the shared inboxes. This
            will not archive in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  if (assignee) {
    if (!isUserAssignee) {
      return (
        <Plural
          id="inboxes-triage-archive-body-plural-assigned-to-other"
          value={inboxes.length}
          one={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to archive in the <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared
              inbox? This will not archive in subscribers&apos; inboxes{trailingPart}.
            </>
          }
          other={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to archive in the shared inboxes? This will not archive in
              subscribers&apos; inboxes{trailingPart}.
            </>
          }
        />
      );
    }

    return (
      <Plural
        id="inboxes-triage-archive-body-plural-assigned-self"
        value={inboxes.length}
        one={
          <>
            Since you&apos;re the assignee, this conversation will also be archived in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not archive in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Since you&apos;re the assignee, this conversation will also be archived in the shared inboxes.
            This will not archive in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  return (
    <Plural
      id="inboxes-triage-archive-body-plural-default"
      value={inboxes.length}
      one={
        <>
          Your teammates will see this conversation as archived in the{' '}
          <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not archive in
          subscribers&apos; inboxes{trailingPart}.
        </>
      }
      other={
        <>
          Your teammates will see this conversation as archived in the shared inboxes. This will not archive
          in subscribers&apos; inboxes{trailingPart}.
        </>
      }
    />
  );
}

function renderTrashBody(options: TriageBodyRendererOptions) {
  const {props, BoldWrapper, trailingPart} = options;
  const {inboxes, conversations} = props;
  const {assignee, isUserAssignee} = props.assignProps;

  const conversationsCount = conversations.length;

  if (conversationsCount > 1) {
    return (
      <Plural
        id="inboxes-triage-archive-trash-plural-default"
        value={inboxes.length}
        one={
          <>
            Your teammates will see {conversationsCount} conversations as trashed in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not trash in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Your teammates will see {conversationsCount} conversations as trashed in the shared inboxes. This
            will not trash in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  if (assignee) {
    if (!isUserAssignee) {
      return (
        <Plural
          id="inboxes-triage-archive-trash-plural-assigned-to-other"
          value={inboxes.length}
          one={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to trash in the <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared
              inbox? This will not trash in subscribers&apos; inboxes{trailingPart}.
            </>
          }
          other={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to trash in the shared inboxes? This will not trash in subscribers&apos;
              inboxes{trailingPart}.
            </>
          }
        />
      );
    }

    return (
      <Plural
        id="inboxes-triage-archive-trash-plural-assigned-to-self"
        value={inboxes.length}
        one={
          <>
            Since you&apos;re the assignee, this conversation will also be trashed in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not trash in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Since you&apos;re the assignee, this conversation will also be trashed in the shared inboxes. This
            will not trash in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  return (
    <Plural
      id="inboxes-triage-archive-trash-plural-default"
      value={inboxes.length}
      one={
        <>
          Your teammates will see this conversation as trashed in the{' '}
          <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not trash in subscribers&apos;
          inboxes{trailingPart}.
        </>
      }
      other={
        <>
          Your teammates will see this conversation as trashed in the shared inboxes. This will not trash in
          subscribers&apos; inboxes{trailingPart}.
        </>
      }
    />
  );
}

function renderSnoozeBody(options: TriageBodyRendererOptions) {
  const {props, BoldWrapper, trailingPart} = options;
  const {inboxes, conversations} = props;
  const {assignee, isUserAssignee} = props.assignProps;

  const conversationsCount = conversations.length;

  if (conversationsCount > 1) {
    return (
      <Plural
        id="inboxes-triage-archive-snooze-plural"
        value={inboxes.length}
        one={
          <>
            Your teammates will see {conversationsCount} conversations as snooze in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not snooze in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Your teammates will see {conversationsCount} conversations as snooze in the shared inboxes. This
            will not snooze in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  if (assignee) {
    if (!isUserAssignee) {
      return (
        <Plural
          id="inboxes-triage-archive-snooze-plural-assigned-to-other"
          value={inboxes.length}
          one={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to snooze in the <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared
              inbox? This will not snooze in subscribers&apos; inboxes{trailingPart}.
            </>
          }
          other={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to snooze in the shared inboxes? This will not snooze in
              subscribers&apos; inboxes{trailingPart}.
            </>
          }
        />
      );
    }

    return (
      <Plural
        id="inboxes-triage-archive-snooze-plural-assigned-to-self"
        value={inboxes.length}
        one={
          <>
            Since you&apos;re the assignee, this conversation will also be snoozed in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not snooze in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Since you&apos;re the assignee, this conversation will also be snoozed in the shared inboxes. This
            will not snooze in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  return (
    <Plural
      id="inboxes-triage-archive-snooze-plural-default"
      value={inboxes.length}
      one={
        <>
          Your teammates will see this conversation as snoozed in the{' '}
          <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not snooze in subscribers&apos;
          inboxes{trailingPart}.
        </>
      }
      other={
        <>
          Your teammates will see this conversation as snoozed in the shared inboxes. This will not snooze in
          subscribers&apos; inboxes{trailingPart}.
        </>
      }
    />
  );
}

function renderSpamBody(options: TriageBodyRendererOptions) {
  const {props, BoldWrapper, trailingPart} = options;
  const {inboxes, conversations} = props;
  const {assignee, isUserAssignee} = props.assignProps;

  const conversationsCount = conversations.length;

  if (conversationsCount > 1) {
    return (
      <Plural
        id="inboxes-triage-archive-spam-plural"
        value={inboxes.length}
        one={
          <>
            Your teammates will see {conversationsCount} conversations as spam in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not mark as spam in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Your teammates will see {conversationsCount} conversations as spam in the shared inboxes. This
            will not mark as spam in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  if (assignee) {
    if (!isUserAssignee) {
      return (
        <Plural
          id="inboxes-triage-archive-spam-assigned-to-other"
          value={inboxes.length}
          one={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to mark as spam in the <BoldWrapper>{inboxes[0].name}</BoldWrapper>{' '}
              shared inbox? This will not mark as spam in subscribers&apos; inboxes{trailingPart}.
            </>
          }
          other={
            <>
              This conversation is assigned to <BoldWrapper>{buildTeammateDisplayName(assignee)}</BoldWrapper>
              . Are you sure you want to mark as spam in the shared inboxes? This will not mark as spam in
              subscribers&apos; inboxes{trailingPart}.
            </>
          }
        />
      );
    }

    return (
      <Plural
        id="inboxes-triage-archive-spam-assigned-to-self"
        value={inboxes.length}
        one={
          <>
            Since you&apos;re the assignee, this conversation will also be marked as spam in the{' '}
            <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not mark as spam in
            subscribers&apos; inboxes{trailingPart}.
          </>
        }
        other={
          <>
            Since you&apos;re the assignee, this conversation will also be marked as spam in the shared
            inboxes. This will not mark as spam in subscribers&apos; inboxes{trailingPart}.
          </>
        }
      />
    );
  }

  return (
    <Plural
      id="inboxes-triage-archive-spam-plural-default"
      value={inboxes.length}
      one={
        <>
          Your teammates will see this conversation as spam in the{' '}
          <BoldWrapper>{inboxes[0].name}</BoldWrapper> shared inbox. This will not mark as spam in
          subscribers&apos; inboxes{trailingPart}.
        </>
      }
      other={
        <>
          Your teammates will see this conversation as spam in the shared inboxes. This will not mark as spam
          in subscribers&apos; inboxes{trailingPart}.
        </>
      }
    />
  );
}

export function renderTriageDialogTitle(props: TriageTeamConfirmationDialogInheritableProps) {
  const {assignProps, conversations} = props;
  const {assignee, isUserAssignee} = assignProps;

  if (!assignee || isUserAssignee || conversations.length > 1) {
    return triageActionDialogTitleRenderers[props.action](props.inboxes);
  }

  return <Trans id="triageactions-assignedconversation">Assigned conversation</Trans>;
}

export function renderTriageDialogBody(
  props: TriageTeamConfirmationDialogInheritableProps,
  BoldWrapper: ComponentType<PropsWithChildren<unknown>>,
  trailingPart?: ReactNode,
) {
  switch (props.action) {
    case TriageActionsEnum.ARCHIVE:
      return renderArchiveBody({props, BoldWrapper, trailingPart});
    case TriageActionsEnum.TRASH:
      return renderTrashBody({props, BoldWrapper, trailingPart});
    case TriageActionsEnum.SNOOZE:
      return renderSnoozeBody({props, BoldWrapper, trailingPart});
    case TriageActionsEnum.SPAM:
      return renderSpamBody({props, BoldWrapper, trailingPart});
    default:
      return null;
  }
}

export function makeGetAssignProps<P>(
  getCellsContext: CellsContextSelector<P>,
  getConversations: ConversationBasesSelector<P>,
) {
  return createSelector(
    [getCellsContext, getConversations, getFindTeammate],
    (cellsContext, conversations, findTeammate) => {
      const isUserAssignee = conversations.some((c) => c.assigneeId === cellsContext.teammateId);
      const assignee = _(conversations)
        .map((c) => c.assigneeId)
        .compact()
        .map((id) => findTeammate(id))
        .compact()
        .first();

      return {
        isUserAssignee,
        assignee,
      };
    },
  );
}
